import { isError, isErrorLike, isNull, isObject, isString } from './is-type';

export function enhanceError(error: unknown, { prefix }: { prefix: string }) {
  if (isError(error)) {
    error.message = `${prefix}: ${error.message}`;
    return error;
  }

  if (isObject(error) && 'message' in error) {
    const { message, ...otherData } = error;
    const enhanced = new Error(`${prefix}: ${String(message)}`);
    Object.assign(enhanced, otherData);
    return enhanced;
  }

  return new Error(`${prefix}: ${isString(error) ? error : `${error}`}`);
}

/**
 * The error code an AWS failure carries, whichever SDK generation threw it.
 *
 * The key moved between generations. AWS SDK v2 and `aws-amplify@4` put the
 * code on `code`; a v3 service exception (`@aws-sdk/client-*`) puts it on
 * `name` and leaves `code` undefined. So `isAwsError`, which tests for `code`,
 * answers `false` for every v3 exception -- and a `switch (e.code)` behind it
 * is unreachable rather than merely unmatched. That is not hypothetical: it is
 * why a clinician inviting an existing patient was shown
 * `Cannot insert_login_user due to:UsernameExistsException: User already exists`
 * instead of the translated message the handler already had a branch for
 * (INSIGHTS-UI-2TF).
 *
 * Read the code through here rather than off either key. `isAwsError` is
 * deliberately left alone: `ErrorBoundary` and `LoginPage` narrow
 * `aws-amplify@4` errors with it and genuinely do have `code`, so widening the
 * guard to accept `name` would have moved the defect to the login page instead
 * of fixing it.
 *
 * ⚠️ This is a code accessor, not a type guard -- it cannot tell an AWS error
 * from any other object with a `name`, and `awsErrorCode(new Error('x'))` is
 * `'Error'`. Use it to drive a `switch` whose unmatched path is already
 * correct, never as the test for "did AWS throw this".
 */
export function awsErrorCode(value: unknown): string | undefined {
  if (typeof value !== 'object' || isNull(value)) {
    return undefined;
  }

  if ('code' in value && isString(value.code)) {
    return value.code;
  }

  if ('name' in value && isString(value.name)) {
    return value.name;
  }

  return undefined;
}

/**
 * The three messages a browser engine uses when `fetch` itself rejects.
 *
 * Each entry is the message exactly as its engine writes it, punctuation
 * included -- Firefox ends its own with a full stop, so the literal does too.
 * These are also, and not by coincidence, the exact three literals the Sentry
 * SDK looks for when it rewrites the message; see
 * `SENTRY_FETCH_HOST_SUFFIX` below.
 */
const FETCH_REJECTION_MESSAGES = new Set([
  // Chromium's wording.
  'Failed to fetch',
  // WebKit's.
  'Load failed',
  // Firefox's, full stop and all. Not observed in prod in the 90 days to
  // 2026-09-22; kept so a Firefox user's dropped connection is classified the
  // same way.
  'NetworkError when attempting to fetch resource.',
]);

/**
 * The messages that mean a request never completed.
 *
 * Nothing is normalised before the lookup except the host suffix below:
 * stripping a trailing full stop to spare one entry would also accept
 * `Load failed.` and `Network error.`, which no engine emits but a server
 * message relayed by `Api.httpRequest` could. An engine that reworded its
 * message would fall out of the table and report at error level again, which is
 * the safe direction to fail in.
 */
const TRANSPORT_FAILURE_MESSAGES = new Set([
  // Observed in prod. `amazon-cognito-identity-js` catches the `TypeError` its
  // own `fetch` rejects with and rethrows this fixed string -- INSIGHTS-UI-K9,
  // 6,163 events. It is a rethrow, not a `fetch` rejection, so the Sentry SDK
  // never rewrites it and it is deliberately not in the set above.
  'Network error',
  ...FETCH_REJECTION_MESSAGES,
]);

/**
 * The ` (host)` the Sentry SDK appends to a `fetch` rejection's message.
 *
 * The suffix is **not** the browser's. `@sentry/core`'s fetch instrumentation
 * rewrites `error.message` in place -- `instrument/fetch.ts`, on the rejection
 * path, gated by the `enhanceFetchErrorMessages` option, which defaults to
 * `'always'` and is not set in `Logger.ts`. It compares the message against the
 * same three literals as `FETCH_REJECTION_MESSAGES`, appends
 * `` ` (${new URL(url).host})` ``, and rethrows the same object -- so every call
 * site downstream sees the rewritten message, not the engine's own.
 *
 * That is why the bare forms all but stopped: the suffix arrived with the
 * `@sentry/react` 7.120 -> 10.68 bump (1aab5c509), not with a browser release.
 * INSIGHTS-UI-JB's bare `Load failed` was last seen 2026-08-26 -- on iOS 26.6.0,
 * so "an older client" is not the explanation -- while INSIGHTS-UI-J4 carries
 * both `Load failed (api-new.insights.allurion.com)` (Mobile Safari) and
 * `Failed to fetch (api-new.insights.allurion.com)` (Chrome, Chrome Mobile,
 * Edge, Samsung Internet). Matching only WebKit's would leave every other
 * engine's transport failure reporting at error level.
 *
 * `URL.host` is `hostname[:port]`: an IPv6 literal brings brackets but nothing
 * here brings a space or a parenthesis, so the suffix is one unbroken token.
 * Stripping only that shape keeps the lookup as tight as an exact table --
 * `Load failed to parse config` is untouched, and a relayed server message
 * ending in a parenthesised phrase does not reduce to a table entry, because a
 * phrase has spaces in it.
 */
const SENTRY_FETCH_HOST_SUFFIX = / \([^\s()]+\)$/;

/**
 * Whether a failure is the request never completing, rather than a response we
 * did not like.
 *
 * `fetch` rejects with a bare `TypeError` carrying no status, no response and
 * no cause, so the message is the only thing that distinguishes the reason --
 * and every engine words it differently. Hence a table of literals rather than
 * a pattern: a substring test for "network" would also catch
 * `NetworkingError` and any server message containing the word.
 *
 * ⚠️ **A match is not proof the user's connection was at fault.** Chromium
 * reports a CORS rejection and a CSP block with the same `Failed to fetch`, and
 * both of those are defects of ours -- `worker-src blob:` broke
 * `insights.allurion.com` exactly that way (CSP-REPORTS-8). That is why the
 * call sites *demote* these to `warning` under a stable fingerprint instead of
 * dropping them: a misconfiguration still arrives, as a spike in one countable
 * issue, rather than being discarded. Never use this to decide not to report.
 */
export function isNetworkError(value: unknown): boolean {
  // `isErrorLike` asserts the shape, not the types -- `'message' in value` is
  // true of a numeric `message` too, and the predicate it returns says
  // otherwise. The lookup below would merely miss on one; the explicit check is
  // what keeps this total if the comparison is ever made to normalise the
  // message, since `.toLowerCase()` on a number throws. Nothing observes it
  // today, so treat it as a guard rail rather than a branch under test.
  if (!isErrorLike(value) || !isString(value.message)) {
    return false;
  }

  const { message } = value;

  // The suffixed form is only accepted for the three literals the SDK actually
  // rewrites, so `Network error (something)` -- which the SDK cannot produce --
  // still reports at error level.
  return (
    TRANSPORT_FAILURE_MESSAGES.has(message) ||
    FETCH_REJECTION_MESSAGES.has(message.replace(SENTRY_FETCH_HOST_SUFFIX, ''))
  );
}
